import React, { useState, useEffect, useMemo, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import AcademyHeader from "./components/MentorshipHeader";
import HomePageEnhanced from "./components/HomePageEnhanced";
import AboutPage from "./components/AboutPage";
import LibraryPage from "./components/LibraryPage";
import GivePage from "./components/GivePage";
import ContactPage from "./components/ContactPage";
import Dashboard from "./components/Dashboard";
import CoursesPage from "./components/CoursesPage";
import CourseViewerPage from "./components/CourseViewerPage";
import CommunityPage from "./components/CommunityPage";
import UploadsPage from "./components/UploadsPage";
import AppointmentsPage from "./components/AppointmentsPage";
import ProfilePageEnhanced from "./components/ProfilePageEnhanced";
import SettingsPage from "./components/SettingsPage";
import EnrollPage from "./components/EnrollPage";
import PaymentSuccess from "./components/PaymentSuccess";
import PaymentCancel from "./components/PaymentCancel";
import MentorDashboardEnhanced from "./components/MentorDashboardEnhanced";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import RenewalPage from "./components/RenewalPage";
import ErrorBoundary from "./components/ErrorBoundary";
import NotFoundPage from "./components/NotFoundPage";
import AdminContentManager from "./components/AdminContentManager";
import MentorshipProgramsOverview from "./components/MentorshipProgramsOverview";
import ApostolicPropheticTrack from "./components/ApostolicPropheticTrack";
import MinstrelWorshipTrack from "./components/MinstrelWorshipTrack";
import { SignUpPage, LoginPage } from "./components/AuthPages";
import VineVoiceBubble from "./components/VineVoiceBubble";
import AssessmentPage from "./components/AssessmentPage";
import AssessmentResultsPage from "./components/AssessmentResultsPage";
import WaitlistPage from "./components/WaitlistPage";
import MentorAppointmentsManagement from "./components/MentorAppointmentsManagement";
import CourseVideoPlayer from "./components/CourseVideoPlayer";
import ExamPage from "./components/ExamPage";
import CourseDebugPage from "./components/CourseDebugPage";
import PricingPage from "./components/PricingPage";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import AppDownloadPage from "./components/AppDownloadPage";
import PWAWelcomePage from "./components/PWAWelcomePage";
import SecureEbookReader from "./components/SecureEbookReader";
import AdminPaymentsPage from "./components/AdminPaymentsPage";
import LiveSessionsPage from "./components/LiveSessionsPage";
import AdminUserAccessPage from "./components/AdminUserAccessPage";
import NotificationsPage from "./components/NotificationsPage";
import AssignmentsPage from "./components/AssignmentsPage";
import PaymentPage from "./components/PaymentPage";
import AdminUploadsPage from "./components/AdminUploadsPage";
import AdminLibraryPage from "./components/AdminLibraryPage";
import UnifiedControlCenter from "./components/UnifiedControlCenter";
import MenteeProfile from "./components/MenteeProfile";
import MySubmissions from "./components/MySubmissions";
import MyCertificates from "./components/MyCertificates";
import SessionRecordings from "./components/SessionRecordings";
import CodexApp from "./codex/CodexApp";

// Check if running as PWA (standalone mode)
const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
              window.navigator.standalone === true;

// Register Service Worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then((registration) => {
        console.log('SW registered:', registration.scope);
      })
      .catch((error) => {
        console.log('SW registration failed:', error);
      });
  });
}

// Auth Context
const AuthContext = React.createContext();

const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Use a ref for activity tracking so the inactivity-watcher effect doesn't
  // re-attach window listeners on every keystroke/mousemove (perf + react-hooks/purity).
  const lastActivityRef = useRef(Date.now());

  // Auto-logout after 30 minutes of inactivity
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const INACTIVITY_TIMEOUT = 30 * 60 * 1000;

    const checkInactivity = () => {
      if (Date.now() - lastActivityRef.current > INACTIVITY_TIMEOUT) {
        console.log('Session expired due to inactivity');
        logout();
      }
    };

    const interval = setInterval(checkInactivity, 60 * 1000);

    const updateActivity = () => { lastActivityRef.current = Date.now(); };

    window.addEventListener('mousedown', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('scroll', updateActivity);
    window.addEventListener('touchstart', updateActivity);

    return () => {
      clearInterval(interval);
      window.removeEventListener('mousedown', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('scroll', updateActivity);
      window.removeEventListener('touchstart', updateActivity);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const processAuth = async () => {
      // Check for session_id in URL fragment (from OAuth redirect)
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        
        try {
          // Call backend to process session_id
          const backendUrl = process.env.REACT_APP_BACKEND_URL;
          const response = await fetch(`${backendUrl}/api/oauth/session-data`, {
            method: 'POST',
            headers: {
              'X-Session-ID': sessionId
            }
          });
          
          if (response.ok) {
            const sessionData = await response.json();
            
            // Store session token (backend sets httpOnly cookie, but we also store in localStorage for client-side checks)
            localStorage.setItem('access_token', sessionData.session_token);
            localStorage.setItem('user_data', JSON.stringify({
              id: sessionData.id,
              email: sessionData.email,
              name: sessionData.name,
              profile_picture: sessionData.picture
            }));
            
            setIsAuthenticated(true);
            setUser({
              id: sessionData.id,
              email: sessionData.email,
              name: sessionData.name,
              profile_picture: sessionData.picture
            });
            
            // Clean URL fragment
            window.location.hash = '';
            
            // Stay on current page (dashboard or wherever they were redirected to)
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('OAuth session processing failed:', error);
        }
      }
      
      // Check for stored auth token on app load
      const token = localStorage.getItem('access_token');
      const userData = localStorage.getItem('user_data');
      
      if (token && userData) {
        setIsAuthenticated(true);
        setUser(JSON.parse(userData));
      }
      setLoading(false);
    };
    
    processAuth();
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('access_token', token);
    localStorage.setItem('user_data', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_data');
    setIsAuthenticated(false);
    setUser(null);
  };

  // Memoise so consumers don't re-render on every parent re-render
  const ctxValue = useMemo(
    () => ({ isAuthenticated, user, login, logout, loading }),
    [isAuthenticated, user, loading]
  );

  return (
    <AuthContext.Provider value={ctxValue}>
      {children}
    </AuthContext.Provider>
  );
};

const PublicLayout = ({ children }) => {
  const navigate = useNavigate();
  
  const handleSignUp = () => navigate('/signup');
  const handleLogin = () => navigate('/login');

  return (
    <>
      <AcademyHeader 
        isAuthenticated={false} 
        user={null}
        onSignUp={handleSignUp}
        onLogin={handleLogin}
      />
      {React.cloneElement(children, { onSignUp: handleSignUp, onLogin: handleLogin })}
    </>
  );
};

const ProtectedLayout = ({ children, user }) => {
  return (
    <>
      <AcademyHeader 
        isAuthenticated={true} 
        user={user}
      />
      {React.cloneElement(children, { user })}
      <VineVoiceBubble />
    </>
  );
};

// Protected Route Wrapper Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (!loading && !isAuthenticated) {
      // Store the attempted URL to redirect back after login
      navigate('/login', { state: { from: location.pathname }, replace: true });
    }
  }, [loading, isAuthenticated, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <ProtectedLayout user={user}>
      {children}
    </ProtectedLayout>
  );
};

const AppContent = () => {
  const { isAuthenticated, user, loading } = React.useContext(AuthContext);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={
        isAuthenticated ? (
          <>
            <AcademyHeader 
              isAuthenticated={true} 
              user={user}
            />
            <HomePageEnhanced />
          </>
        ) : isPWA ? (
          <PWAWelcomePage />
        ) : (
          <PublicLayout>
            <HomePageEnhanced />
          </PublicLayout>
        )
      } />
      
      <Route path="/about" element={
        <PublicLayout>
          <AboutPage />
        </PublicLayout>
      } />
      
      <Route path="/welcome" element={<PWAWelcomePage />} />
      
      <Route path="/give" element={
        <PublicLayout>
          <GivePage />
        </PublicLayout>
      } />
      
      <Route path="/contact" element={
        <PublicLayout>
          <ContactPage />
        </PublicLayout>
      } />
      
      <Route path="/mentorship-programs" element={
        <PublicLayout>
          <MentorshipProgramsOverview />
        </PublicLayout>
      } />
      
      <Route path="/mentorship-programs/apostolic-prophetic" element={
        <PublicLayout>
          <ApostolicPropheticTrack />
        </PublicLayout>
      } />
      
      <Route path="/mentorship-programs/minstrel-worship" element={
        <PublicLayout>
          <MinstrelWorshipTrack />
        </PublicLayout>
      } />
      
      {/* Assessment & Waitlist Routes */}
      <Route path="/assessment" element={<AssessmentPage />} />
      <Route path="/assessment/results" element={<AssessmentResultsPage />} />
      <Route path="/waitlist" element={<WaitlistPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/download" element={<AppDownloadPage />} />
      <Route path="/get-app" element={<AppDownloadPage />} />

      {/* CODEX: The Ancient Scrolls — standalone immersive layout */}
      <Route path="/codex/*" element={<CodexApp />} />
      
      {/* Mentor Routes */}
      <Route path="/mentor/appointments" element={
        <ProtectedLayout>
          <MentorAppointmentsManagement />
        </ProtectedLayout>
      } />
      
      <Route path="/signup" element={<SignUpPage onBack={() => window.history.back()} />} />
      <Route path="/login" element={<LoginPage onBack={() => window.history.back()} />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/enroll" element={<EnrollPage />} />
      <Route path="/renewal" element={<RenewalPage />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/payment-cancel" element={<PaymentCancel />} />

      {/* Protected Routes - Always defined, auth checked inside ProtectedRoute */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/courses" element={
        <ProtectedRoute>
          <CoursesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/courses/:courseId" element={
        <ProtectedRoute>
          <CourseVideoPlayer />
        </ProtectedRoute>
      } />
      
      <Route path="/library" element={
        <ProtectedRoute>
          <LibraryPage />
        </ProtectedRoute>
      } />
      
      <Route path="/library/read/:ebookId" element={
        <ProtectedRoute>
          <SecureEbookReader />
        </ProtectedRoute>
      } />
      
      <Route path="/community" element={
        <ProtectedRoute>
          <CommunityPage />
        </ProtectedRoute>
      } />
      
      <Route path="/uploads" element={
        <ProtectedRoute>
          <UploadsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/appointments" element={
        <ProtectedRoute>
          <AppointmentsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePageEnhanced />
        </ProtectedRoute>
      } />
      
      <Route path="/mentor" element={
        <ProtectedRoute>
          <MentorDashboardEnhanced />
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/notifications" element={
        <ProtectedRoute>
          <NotificationsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/assignments" element={
        <ProtectedRoute>
          <AssignmentsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/payments" element={
        <ProtectedRoute>
          <PaymentPage />
        </ProtectedRoute>
      } />
      
      <Route path="/live-sessions" element={
        <ProtectedRoute>
          <LiveSessionsPage user={user} />
        </ProtectedRoute>
      } />
      
      <Route path="/progress" element={
        <ProtectedRoute>
          <div className="pt-20 p-8"><h1>My Progress (Coming Soon)</h1></div>
        </ProtectedRoute>
      } />
      
      {/* My Submissions & Feedback Page */}
      <Route path="/my-submissions" element={
        <ProtectedRoute>
          <MySubmissions />
        </ProtectedRoute>
      } />
      
      {/* My Certificates Page */}
      <Route path="/my-certificates" element={
        <ProtectedRoute>
          <MyCertificates />
        </ProtectedRoute>
      } />
      
      {/* Session Recordings Page */}
      <Route path="/recordings" element={
        <ProtectedRoute>
          <SessionRecordings />
        </ProtectedRoute>
      } />
      <Route path="/session-recordings" element={
        <ProtectedRoute>
          <SessionRecordings />
        </ProtectedRoute>
      } />
      
      {/* Admin Content Manager - Admin Only */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminContentManager />
        </ProtectedRoute>
      } />
      <Route path="/admin/content" element={
        <ProtectedRoute>
          <AdminContentManager />
        </ProtectedRoute>
      } />
      <Route path="/admin/payments" element={
        <ProtectedRoute>
          <AdminPaymentsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/user-access" element={
        <ProtectedRoute>
          <AdminUserAccessPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/uploads" element={
        <ProtectedRoute>
          <AdminUploadsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/library" element={
        <ProtectedRoute>
          <AdminLibraryPage />
        </ProtectedRoute>
      } />
      
      {/* Unified Control Center - Preview */}
      <Route path="/control-center" element={
        <ProtectedRoute>
          <UnifiedControlCenter user={user} />
        </ProtectedRoute>
      } />
      
      {/* Mentee Profile Page */}
      <Route path="/control-center/mentee/:menteeId" element={
        <ProtectedRoute>
          <MenteeProfile />
        </ProtectedRoute>
      } />
      
      {/* Course Debug Page */}
      <Route path="/course-debug/:courseId" element={
        <ProtectedRoute>
          <CourseDebugPage />
        </ProtectedRoute>
      } />
      
      {/* Course Video Player Routes */}
      <Route path="/course/:courseId" element={
        <ProtectedRoute>
          <CourseVideoPlayer />
        </ProtectedRoute>
      } />
      
      {/* Exam Routes */}
      <Route path="/exam/:lessonId" element={
        <ProtectedRoute>
          <ExamPage />
        </ProtectedRoute>
      } />
      
      {/* 404 - Catch all unmatched routes */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <AppContent />
            <PWAInstallPrompt />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
